import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL;

// Create axios instance with improved configuration
export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Set a very long timeout (30 minutes) to avoid blocking long-running API calls
  // This ensures we don't timeout on the frontend side for operations that take time
  // The backend will handle its own timeout limits
  timeout: 30 * 60 * 1000, // 30 minutes in milliseconds
});

// Debug logging removed to reduce console noise

// Custom event for auth navigation
export const AUTH_NAVIGATION_EVENT = 'auth_navigation';

// Request interceptor with improved error handling
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Remove Content-Type header from GET requests to prevent backend/proxy body-parsing timeouts
    if (config.method?.toLowerCase() === 'get') {
      if (config.headers) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
          config.headers.delete('content-type');
        } else {
          delete config.headers['Content-Type'];
          delete config.headers['content-type'];
        }
      }
    }
    
    if (import.meta.env.DEV) {
      console.log('API Request:', config.method?.toUpperCase(), config.url);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor (no response body cache — data freshness is handled by React Query)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle unauthorized errors
    // Handle unauthorized errors
    if (error.response?.status === 401) {
      console.warn('[API] 401 Unauthorized detected. Session details:', {
        url: originalRequest?.url,
        hasToken: !!sessionStorage.getItem('token'),
        tokenCreationTime: sessionStorage.getItem('tokenCreationTime'),
        tokenAgeMinutes: Math.round((Date.now() - parseInt(sessionStorage.getItem('tokenCreationTime') || '0')) / 1000 / 60)
      });

      // Clear session storage
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tokenCreationTime');
      
      // Dispatch custom event for navigation
      window.dispatchEvent(new CustomEvent(AUTH_NAVIGATION_EVENT, {
        detail: { path: '/login', search: '?expired=true' }
      }));
      
      return Promise.reject(error);
    }

    // Only retry transient network errors or 5xx server errors
    // Do not retry 4xx client errors (400, 403, 404, 422, etc.) as they are non-transient
    const isClientError = error.response && error.response.status < 500;

    if (!isClientError && !originalRequest._retry) {
      originalRequest._retry = true;
      return api(originalRequest);
    }

    // Log detailed error information
    console.error('API Error:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      data: error.response?.data,
      error: error.message
    });

    return Promise.reject(error);
  }
);

/** @deprecated Axios no longer caches GET bodies; kept for callers that still invoke it. */
export const clearCache = () => {};

/** @deprecated Axios no longer caches GET bodies; kept for callers that still invoke it. */
export const clearCacheEntry = (_url) => {};

// Utility to safely join base URL and endpoint
export function joinUrl(base, path) {
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

export const endpoints = {
  // Auth endpoints
  login: '/token',
  loginWith2fa: '/admin/login-with-2fa',
  superAdminStatusCheck: '/super-admin-status-check',
  superAdminRegister: '/super-admin/register',
  refreshToken: '/refresh-token',
  
  // NetSuite sync endpoints
  netsuiteLocations: '/netsuite/locations',
  netsuiteBins: '/netsuite/bins',
  netsuiteInventory: '/netsuite/inventory',
  netsuiteItems: '/netsuite/items',
  netsuiteSalesOrders: '/netsuite/sales-orders',
  netsuiteInboundData: '/get-inbound-data',
  
  // Data endpoints
  locations: '/data_locations',
  bins: '/data_bins',
  admins: '/master-admin-details',
  inventory: '/data_inventory',
  items: '/data_items',
  salesOrders: '/data_sales_orders',
  netsuiteSalesOrderScreen: '/get-netsuite-sales-order',
  netsuiteSoLineLevel: '/netsuite-so-line-level',
  dropshipSalesOrders: '/get-dropship-sales-order',
  salesOrderList: '/get-sales-order-list',
  salesOrderLineLevel: '/sales-order-line-level',
  dropshipUpdatePriority:
    'https://jy-moment-scheduler-production.up.railway.app/dropship-update-priority',

  amxlDashboard: '/amxl-dashboard',
  atsDashboard: '/ats-dashboard',
  upsDashboard: '/ups-dashboard',
  dropshipLines: '/dropship-lines',
  dropshipSoLineLevel: '/dropship-so-line-level',
  binCountRecords: '/bin-count-records',
  wmsAiUsers: '/wms-ai-users',
  masterAdminDetails: '/master-admin-details',
  roles: '/roles',
  rolesCreate: '/roles',
  rolesUpdate: '/roles', // PATCH /roles/{role_name} — path must be role name string
  rolesDelete: '/roles', // DELETE /roles/{role_name} — path must be role name string
  rolesAssign: '/roles/assign',
  rolesAssignBulk: '/roles/assign-bulk',
  rolesAssignUsers: '/roles/assign-users',
  rolesRemoveUser: '/roles/users', // DELETE /roles/users/{userid}/role
  assignRoleToUser: '/roles/assign-role-to-user',
  binAdjustmentRecords: '/bin-adjustment-records',
  binTransfer: '/bin-transfer-records',
  inboundShipments: '/get-inbound-data-records',
  inboundDataRecords: '/inbound-data-records',
  
  // Scrape endpoints
  homeDepotProducts: '/homedepot_products',
  homeDepotProductsErrors: '/homedepot_products_errors',
  scrapeFiles: '/scrape_files',
  
  // LTL endpoints
  ltlRecords: '/data-get-ltl-records',
  ltlPalletMap: '/ltl-pallet-map',
  ltlInsufficientInventory: '/insufficient-inventory',
  pickedQuantityReport: '/picked-quantity-report',
  partsDashboard: '/parts/dashboard',
  ltlPickProgress: '/ltl/pick-progress',
  ltlPickSequence: '/ltl/pick-sequence',
  dropshipPickProgress: '/dropship-pick-progress',
  dropshipNextItems: '/dropship-next-items',
  // FedEx files upload
  fedexFilesUpload: '/fedex/files/upload',
  
  // FedEx shipping analysis
  fedexShippingAnalysis: 'https://fedexstats-production.up.railway.app/run-shipping-analysis?format=json',
  priceComparison: 'https://scrapers-price-comparison-production.up.railway.app/api/price-comparison',
  // Errors endpoints
  errorsStatus: '/get-netsuite-error-status',
  errorsRetryAll: '/get-netsuite-error-status-retry-all',
  errorsRetryOne: '/get-netsuite-error-status-retry',
  shippingLabelErrors: '/shipping/label-errors',
  
  // Admin profile endpoints
  adminProfile: '/admin/profile',

  // Item Sales Report — authenticated, customer-scope-enforced proxy to amazon_automations
  itemSalesReport: '/reports/item-sales-by-customer',
  itemSalesReportCustomers: '/reports/item-sales-by-customer/customers',

  // Pricing API – base URL is VITE_PRICING_API_URL (handled in MarketplacePriceUpdate component)
  marketplacePriceUpdate: '/pricing-changes'
};

/** Request body for dropship-update-priority: { data: [{ "internal_id": priority }, ...] } */
export const buildDropshipPriorityRequestBody = (entries) => ({
  data: entries.map(({ internal_id, priority }) => {
    const normalized = Number(priority);
    return { [String(internal_id)]: Number.isFinite(normalized) ? normalized : 0 };
  }),
});

/** Parse scheduler dropship-update-priority response for UI feedback. */
export const parseDropshipPriorityResponse = (response, submittedCount = 0) => {
  const data = response?.data ?? {};
  const httpOk = response?.status >= 200 && response?.status < 300;
  const updatedCount = Number(data.total_rows_updated ?? data.updated_count ?? 0);
  const processedCount = Number(data.total_internal_ids_processed ?? submittedCount);
  const notFound = Array.isArray(data.not_found) ? data.not_found : [];
  const apiMessage =
    data.message ||
    (typeof data.detail === 'string' ? data.detail : null) ||
    (httpOk ? 'Priority update completed.' : 'Priority update failed.');

  const apiCompleted =
    httpOk &&
    (String(apiMessage).toLowerCase().includes('completed') ||
      String(data.status ?? '').toLowerCase() === 'success');

  const displayUpdatedCount =
    updatedCount > 0 ? updatedCount : notFound.length === 0 ? processedCount : updatedCount;

  const isSuccess = httpOk && apiCompleted && notFound.length === 0;
  const isPartialSuccess =
    httpOk && apiCompleted && (notFound.length > 0 || displayUpdatedCount < submittedCount);

  let message = apiMessage;
  if (notFound.length > 0) {
    const preview = notFound.slice(0, 10).join(', ');
    const suffix = notFound.length > 10 ? ` (+${notFound.length - 10} more)` : '';
    message += ` Internal IDs not found: ${preview}${suffix}.`;
  }

  return {
    message,
    updatedCount: displayUpdatedCount,
    processedCount,
    submittedCount,
    notFound,
    status: isSuccess ? 'success' : isPartialSuccess || httpOk ? 'warning' : 'error',
    severity: isSuccess ? 'success' : isPartialSuccess || (httpOk && apiCompleted) ? 'warning' : 'error',
    allUpdated: isSuccess && displayUpdatedCount >= submittedCount,
    shouldClearForm: isSuccess,
  };
};

/** Stable React Query key segment per path; first wins when paths repeat. O(1) lookup. */
const endpointPathToKey = Object.create(null);
for (const [key, path] of Object.entries(endpoints)) {
  if (endpointPathToKey[path] === undefined) {
    endpointPathToKey[path] = key;
  }
}
export const getEndpointCacheKey = (path) => endpointPathToKey[path] || path;

// Utility function to make API calls with proper headers
export const apiCall = async (url, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json',
    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
    ...options.headers
  };

  const config = {
    ...options,
    headers: defaultHeaders,
    credentials: 'include'
  };

  try {
    const response = await fetch(url, config);
    
    // Check if response is ok
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

// Utility function for DELETE requests
export const deleteApiCall = async (endpoint) => {
  const url = joinUrl(import.meta.env.VITE_API_URL, endpoint);
  return apiCall(url, { method: 'DELETE' });
};

// Utility function for POST requests
export const postApiCall = async (endpoint, data) => {
  const url = joinUrl(import.meta.env.VITE_API_URL, endpoint);
  return apiCall(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

// Utility function for PUT requests
export const putApiCall = async (endpoint, data) => {
  const url = joinUrl(import.meta.env.VITE_API_URL, endpoint);
  return apiCall(url, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Utility function for external API calls with basic auth
export const externalApiCall = async (url, username, password) => {
  const credentials = btoa(`${username}:${password}`);
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Basic ${credentials}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  
  return await response.json();
}; 